import React, { useState, useEffect } from 'react';
import { apiFetch, API_BASE } from '../utils/api';
import { FIELD_LABEL_CLASS } from '../constants/inputs';

/**
 * Inline editor for an order's `expected_delivery_date`. Renders a STACKED
 * label ("Expected delivery") above a full-width date input, with the
 * tick/cross save/cancel controls beside the input — no auto-save.
 *
 * 🔀 2026-09-13, THIRD PASS — was a single inline row ("Expected Delivery:
 * [date input] ✓ ✗", modelled on `InlinePoNumberEditor.js`'s shape) while
 * this rendered inside a wide metadata-band track; owner, live in the
 * browser once the second-pass ruling moved it into a HALF `xl:col-span-1`
 * Order Details grid cell: *"the text 'Expected delivery' is too wide to fit
 * under PO Number"*, *"fields overlapping, labels overlapping"*. Restacked
 * to match every other field in that grid (label above, full-width control
 * below) — see the render's own comment for why the inline shape could not
 * be rescued by shrinking the label alone.
 *

 * - Tick saves via the dedicated `PATCH /api/orders/:id/expected-delivery`
 *   (`server/routes/orders.js`), gated `requireDepartmentCapability(...,
 *   order.department_id, 'canManageDepartmentOrders')` — resolved in the
 *   ORDER'S OWN department, never `req.isAdmin`/`req.isBuyer`. Visible only
 *   when the typed value differs from the order's current
 *   `expected_delivery_date`.
 * - Cross reverts the input to the last saved value (or empty if none has
 *   been saved). Same visibility rule.
 * - Enter triggers save; Escape triggers cancel.
 *
 * Used wherever an order MANAGER should be able to record/correct a
 * supplier's delivery ETA without leaving the expanded row. This component
 * deliberately knows nothing about roles or order status; the parent
 * (`OrderExpandedRow.js`) gates BOTH visibility (whether to show an editor at
 * all) and which statuses reach it (`ordered`/`partially_delivered` — never
 * before `ordered`, and `delivered` is read-only via `ExpectedDeliveryBadge`
 * instead of this component — see the owner's ruling at the call site).
 *
 * 🚨 **THE FIELD STARTS BLANK AND STAYS BLANK UNTIL A BUYER TYPES A DATE —
 * owner correction, 2026-08-19, REPLACING an earlier brief that asked for a
 * lead-time-derived PREFILL.** *"expected delivery date is blank and doesnt
 * do anything unless the buyer inputs a value."* No suggestion, no computed
 * default, nothing written or displayed until a human sets it — the same "a
 * form may not invent a value for it" rule `client/CLAUDE.md` records at
 * length for `OrderForm.js`'s old `'Unknown'` lead-time default, satisfied
 * here trivially rather than by a prefill-vs-store distinction.
 *
 * Props:
 * - `order` — order object (must have `id` and optional `expected_delivery_date`).
 * - `onSaved(orderId, normalisedValue)` — fires after a successful PATCH so
 *   the parent can sync its own state (orders list, orderDetails cache, etc).
 * - `testId` — `data-testid` for the input. Differ per page/testIdPrefix.
 */
export default function InlineExpectedDeliveryEditor({ order, onSaved, testId }) {
  const savedValue = order.expected_delivery_date || '';
  const [localValue, setLocalValue] = useState(savedValue);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Re-sync if the parent refetches the order and the date changes externally.
  useEffect(() => {
    setLocalValue(order.expected_delivery_date || '');
  }, [order.expected_delivery_date]);

  const isDirty = String(localValue) !== String(savedValue);

  const handleSave = async () => {
    if (!isDirty || saving) return;
    setSaving(true);
    setError(null);
    try {
      const res = await apiFetch(`${API_BASE}/api/orders/${order.id}/expected-delivery`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ expected_delivery_date: localValue || null }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        setError(body.error || 'Failed to update expected delivery date');
        return;
      }
      const updated = await res.json();
      if (onSaved) onSaved(order.id, updated.expected_delivery_date || null);
    } catch (err) {
      console.error('Failed to update expected delivery date:', err);
      setError('Failed to update expected delivery date');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setLocalValue(savedValue);
    setError(null);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSave();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      handleCancel();
    }
  };

  // 🔀 2026-09-13, THIRD PASS — STACKED, NOT INLINE. Owner (live, in the
  // browser, on the new Order Details row 2): *"the text 'Expected delivery'
  // is too wide to fit under PO Number"* and *"fields overlapping, labels
  // overlapping"*. This component was built for the OLD 14rem-minimum
  // metadata-band track (2026-08-19); the SECOND-PASS ruling the same day
  // (2026-09-13) put it into a HALF `xl:col-span-1` grid cell instead — the
  // same width Needed by and Lead Time already use — and a single inline
  // row of bold label + native date input + two icon buttons cannot fit
  // there, whichever order the flex-wrap breaks it at: a native
  // `<input type="date">` has a browser-enforced minimum content width that
  // does not shrink, so it can render wider than the cell and visually spill
  // into the next one.
  // 🔑 THE FIX MATCHES THE GRID'S OWN CONVENTION — label ABOVE, full-width
  // control BELOW — the same shape `custom_needed_by_date` (also a native
  // date input) already uses at this exact column width with no complaint.
  // The label text also drops its colon and initial caps to match
  // `Needed by` / other field labels' house style, and the sole call site
  // (`OrderExpandedRow.js`) is the only consumer, so nothing else depends on
  // the old inline shape (checked, not assumed — one render call site).
  return (
    <span className="block" onClick={(e) => e.stopPropagation()}>
      <span className={FIELD_LABEL_CLASS}>
        Expected delivery
      </span>
      <span className="flex items-center gap-1 flex-wrap">
      <input
        type="date"
        value={localValue}
        onChange={(e) => setLocalValue(e.target.value)}
        onKeyDown={handleKeyDown}
        className="w-full min-w-0 px-2 py-0.5 text-sm border border-gray-300 dark:border-gray-600 rounded text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
        data-testid={testId}
        aria-label="Expected Delivery Date"
        disabled={saving}
      />
      {isDirty && (
        <>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="p-1 text-green-600 hover:text-green-700 dark:text-green-400 dark:hover:text-green-300 disabled:opacity-50"
            data-testid={testId ? `${testId}-save` : undefined}
            aria-label="Save Expected Delivery Date"
            title="Save (Enter)"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
          </button>
          <button
            type="button"
            onClick={handleCancel}
            disabled={saving}
            className="p-1 text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 disabled:opacity-50"
            data-testid={testId ? `${testId}-cancel` : undefined}
            aria-label="Cancel Expected Delivery Date edit"
            title="Cancel (Esc)"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </>
      )}
      {error && (
        <span className="text-xs text-red-600 dark:text-red-400 basis-full" data-testid={testId ? `${testId}-error` : undefined}>
          {error}
        </span>
      )}
      </span>
    </span>
  );
}
